import { useEffect } from 'react';

import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { useNavigate } from 'react-router-dom';
import { Toaster } from 'sonner';

import ErrorBoundary from './components/ui/ErrorBoundary';
import Router from './router';
import { useAuthStore } from './store/useAuthStore.ts';
import { setNavigateFunction } from './utils/authExpiry';

const App = () => {
  const { checkAuthStatus, initializeCurrentUser } = useAuthStore();
  const navigate = useNavigate();

  // Set up navigation function for auth expiry handling
  useEffect(() => {
    setNavigateFunction(navigate);
  }, [navigate]);

  // Configure StatusBar for mobile devices
  useEffect(() => {
    const setupStatusBar = async () => {
      if (Capacitor.isNativePlatform()) {
        try {
          await StatusBar.setStyle({ style: Style.Light });
          await StatusBar.setBackgroundColor({ color: '#ffffff' });
          await StatusBar.setOverlaysWebView({ overlay: false });
        } catch (error) {
          console.warn('StatusBar setup failed:', error);
        }
      }
    };

    void setupStatusBar();
  }, []);

  // Check auth status on app start and initialize user if authenticated
  useEffect(() => {
    const initializeAuth = async () => {
      // First check if tokens are valid
      const isAuth = await checkAuthStatus();
      // If authenticated, fetch user data
      if (isAuth) {
        await initializeCurrentUser();
      }
    };

    void initializeAuth();
  }, [checkAuthStatus, initializeCurrentUser]);

  return (
    <ErrorBoundary>
      <Toaster 
        position="top-center" 
        toastOptions={{
          style: {
            marginTop: '70px', // Push down to avoid header/menu overlap
          },
        }}
      />
      <Router />
    </ErrorBoundary>
  );
};

export default App;
